import React from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, ChartEvent, ActiveElement, Chart } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { AnalyticsTagData, AnalyticsTagLevel } from '../../types';
import ErrorBoundary from '../ErrorBoundary';

ChartJS.register(ArcElement, Tooltip, Legend);

// Fallback slice colours, matching the other two pie charts. A tag only falls back to these
// when it has no colour of its own set in Admin > Product Tags.
const FALLBACK_COLORS = [
  '#3B7DD8', // accent blue
  '#E07856', // terracotta
  '#4FA37C', // sage green
  '#E3B341', // golden
  '#8E6FC9', // soft violet
  '#56A6C9', // sky teal
  '#D8689B', // rose
  '#7A9E5F', // olive
  '#C98B56', // amber brown
  '#7B8794', // slate grey
];

const isHexColor = (value?: string) => !!value && /^#[0-9A-Fa-f]{6}$/.test(value);

interface AnalyticsTagChartProps {
  tagData: AnalyticsTagData[];
  tagLevel: AnalyticsTagLevel;
  selectedTag: string | null;
  /** Drills into a tag; the whole page filters by it, as with region and customer type. */
  onSelectTag: (tagName: string) => void;
  /** Clears the tag filter, including the matching chip in the filter box. */
  onClearTag: () => void;
  metric: 'sales' | 'orderCount' | 'itemCount';
}

const AnalyticsTagChart: React.FC<AnalyticsTagChartProps> = ({
  tagData,
  tagLevel,
  selectedTag,
  onSelectTag,
  onClearTag,
  metric,
}) => {
  const formatMetric = (value: number, metric: string) => {
    if (metric === 'sales') return `$${value.toFixed(2)}`;
    return value.toString();
  };

  const totalValue = tagData.reduce((sum, d) => sum + (d.value || 0), 0);
  const isDrilledIn = tagLevel === 'detail';

  const pieChartData = {
    labels: tagData.map((d) => d.tag_name || 'Untagged'),
    datasets: [
      {
        data: tagData.map((d) => d.value || 0),
        backgroundColor: tagData.map((d, index) =>
          isHexColor(d.color) ? (d.color as string) : FALLBACK_COLORS[index % FALLBACK_COLORS.length]
        ),
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverBorderColor: 'rgb(255, 255, 255)',
        hoverOffset: 30,
      },
    ],
  };

  // One level of drill-down: a grouping tag (vehicle model) -> the other tags on the products
  // inside it (part types). Those inner slices are the end of the road, because the tag filter
  // matches any of its tags rather than all of them, so stacking a second tag would widen the
  // selection instead of narrowing it.
  const handlePieClick = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
    if (isDrilledIn || elements.length === 0) return;
    const data = tagData[elements[0].index];
    if (data?.tag_name) onSelectTag(data.tag_name);
  };

  const metricLabel = metric === 'sales' ? 'Sales' : metric === 'orderCount' ? 'Orders' : 'Items';
  const title = isDrilledIn && selectedTag
    ? `${metricLabel} by Product Tag: ${selectedTag.split(',').join(', ')}`
    : `${metricLabel} By Product Tag`;

  const emptyMessage = isDrilledIn
    ? 'No other product tags on the products in this selection.'
    : 'No product tags are set to "Use for analytics grouping" yet — set them in Admin > Product Tags.';

  return (
    <div className="analytics-card analytics-pie-card">
      <div className="analytics-card-header">
        <h3>{title}</h3>
        {selectedTag && (
          <button className="analytics-action-button" onClick={onClearTag}>
            Back to Product Tags
          </button>
        )}
      </div>
      <ErrorBoundary>
        <div className="analytics-pie-container">
          {pieChartData.labels.length > 0 && tagData.some((d) => d.value > 0) ? (
            <Doughnut
              data={pieChartData}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      font: {
                        family: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
                        size: 14,
                      },
                      color: '#1f2a44',
                      padding: 20,
                    },
                  },
                  tooltip: {
                    backgroundColor: '#ffffff',
                    titleColor: '#1f2a44',
                    bodyColor: '#1f2a44',
                    borderColor: '#e5e7eb',
                    borderWidth: 1,
                    cornerRadius: 8,
                    padding: 12,
                    callbacks: {
                      label: (context) => {
                        const value = context.raw as number;
                        const percentage = totalValue ? ((value / totalValue) * 100).toFixed(1) : '0';
                        return `${context.label}: ${formatMetric(value, metric)} (${percentage}%)`;
                      },
                    },
                  },
                },
                onClick: handlePieClick,
                hover: {
                  mode: 'nearest',
                  intersect: true,
                },
              }}
            />
          ) : (
            <p>{emptyMessage}</p>
          )}
        </div>
      </ErrorBoundary>
    </div>
  );
};

export default AnalyticsTagChart;
